"use client"

import { CalendarClock } from "lucide-react"
import { StatusBanner, StatusBannerContent, StatusBannerRow } from "@/components/ui/status-banner"
import { useIdentity } from "@/lib/providers/identity"
import { useMyOrganisationLifecycle } from "@/entities/billing/model/api/my-organisation-lifecycle"
import { formatDateTime } from "@/shared/utils/format-datetime"
import { usePublishBannerHeight } from "@/shared/ui/use-publish-banner-height"

const SUPPORT_EMAIL = "team@cova.ai"

/**
 * Member variant of the scheduled-deactivation banner (spec
 * lifecycle/2026-09-30-001 §4.11 members): org admins see it in the app shell
 * while their organisation is `deactivation_scheduled`, so the close date is
 * never a surprise. The staff variant lives in `features/organisation-lifecycle`.
 *
 * Reads `getMyOrganisationLifecycle` only for org admins; the answer carries no
 * reason, note or actor, so neither does the banner. Not dismissible: it is a
 * date the whole organisation loses access on.
 *
 * Publishes its height as `--banner-h-lifecycle` (wrapper is `flow-root` so the
 * StatusBanner's top margin is counted), which `SHELL_HEIGHT_CLASS` subtracts.
 */
export function ScheduledDeactivationMemberBanner() {
  const hostRef = usePublishBannerHeight("lifecycle")
  const { identity } = useIdentity()
  const isOrgAdmin = identity?.isAdmin === true
  const { data } = useMyOrganisationLifecycle(isOrgAdmin)

  if (!isOrgAdmin || data?.state !== "deactivation_scheduled") return null

  const orgName = identity.orgName ?? null
  const closesOn = data.effectiveAt ? formatDateTime(data.effectiveAt) : null
  const subject = encodeURIComponent("Our Cova account is scheduled to close")

  return (
    <div ref={hostRef} className="flow-root" data-testid="scheduled-deactivation-member-banner">
      <StatusBanner color="amber" role="status">
        <StatusBannerRow>
          <StatusBannerContent className="flex items-start gap-3">
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-white/80" aria-hidden />
            <div className="flex-1">
              <p className="text-sm font-medium text-white">
                {orgName ? `${orgName}’s` : "Your organisation’s"} Cova account is scheduled
                to close{closesOn ? ` on ${closesOn}` : " at the end of the billing period"}. Every
                member loses access then.
              </p>
              {data.canContactSupport ? (
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=${subject}`}
                  className="mt-1 inline-flex text-xs font-medium underline underline-offset-2"
                >
                  Questions? Contact support
                </a>
              ) : null}
            </div>
          </StatusBannerContent>
        </StatusBannerRow>
      </StatusBanner>
    </div>
  )
}
